import { useEffect } from 'react';
import { type FieldValues, type UseFormReturn, useFormState } from 'react-hook-form';
import {
  type ConfigFormSaveSlots,
  EMPTY_CONFIG_FORM_SUBMIT_STATE,
} from 'src/pages/Settings/memberships/types';

export const useConfigFormSaveSlots = <TValues extends FieldValues>(
  formMethods: UseFormReturn<TValues>,
  { onSaveRequestChange, onDirtyChange, onSubmitStateChange }: ConfigFormSaveSlots<TValues>,
  /** Memoised, or the save request is replaced on every render. */
  onInvalid: () => void,
) => {
  const { isDirty, isValid, isValidating, isSubmitting } = useFormState({
    control: formMethods.control,
  });

  useEffect(() => {
    return () => {
      onSaveRequestChange(null);
      onDirtyChange(false);
      onSubmitStateChange(EMPTY_CONFIG_FORM_SUBMIT_STATE);
    };
  }, [onSaveRequestChange, onDirtyChange, onSubmitStateChange]);

  useEffect(() => {
    onDirtyChange(isDirty);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    onSubmitStateChange({ isValid, isValidating, isSubmitting });
  }, [isSubmitting, isValid, isValidating, onSubmitStateChange]);

  useEffect(() => {
    onSaveRequestChange(() => async () => {
      const isFormValid = await formMethods.trigger();

      if (!isFormValid) {
        onInvalid();
        return null;
      }

      const values = formMethods.getValues();

      return { values, reset: () => formMethods.reset(values) };
    });
  }, [formMethods, onInvalid, onSaveRequestChange]);
};
